import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, Calendar, Trash2 } from 'lucide-react';
import { eventsApi, CalendarEvent } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

interface EventModalProps {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
  event?: CalendarEvent | null; // For editing
  initialDate?: Date; // For pre-filling date when creating
}

// Predefined color options
const colorOptions = [
  { value: '', label: 'Default', class: 'bg-accent' },
  { value: '#22c55e', label: 'Green', class: 'bg-success' },
  { value: '#f59e0b', label: 'Orange', class: 'bg-warning' },
  { value: '#e61515', label: 'Red', class: 'bg-primary' },
  { value: '#8b5cf6', label: 'Purple', class: 'bg-purple-500' },
  { value: '#06b6d4', label: 'Cyan', class: 'bg-cyan-500' },
];

// Format date for input[type="date"]
function formatDateForInput(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString().split('T')[0];
}

// Format time for input[type="time"]
function formatTimeForInput(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toTimeString().slice(0, 5);
}

export function EventModal({
  isOpen,
  projectId,
  onClose,
  event,
  initialDate,
}: EventModalProps) {
  const queryClient = useQueryClient();
  const isEditing = !!event;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('10:00');
  const [isAllDay, setIsAllDay] = useState(false);
  const [color, setColor] = useState('');
  const [hasEndDate, setHasEndDate] = useState(false);

  // Initialize form when modal opens or event changes
  useEffect(() => {
    if (isOpen) {
      if (event) {
        // Editing existing event
        setTitle(event.title);
        setDescription(event.description || '');
        setStartDate(formatDateForInput(event.startDate));
        setStartTime(formatTimeForInput(event.startDate));
        setIsAllDay(event.isAllDay);
        setColor(event.color || '');

        if (event.endDate) {
          setHasEndDate(true);
          setEndDate(formatDateForInput(event.endDate));
          setEndTime(formatTimeForInput(event.endDate));
        } else {
          setHasEndDate(false);
          setEndDate('');
          setEndTime('10:00');
        }
      } else {
        // Creating new event
        const defaultDate = initialDate || new Date();
        setTitle('');
        setDescription('');
        setStartDate(formatDateForInput(defaultDate));
        setStartTime('09:00');
        setEndDate('');
        setEndTime('10:00');
        setIsAllDay(false);
        setColor('');
        setHasEndDate(false);
      }
    }
  }, [isOpen, event, initialDate]);

  const invalidateEventQueries = () => {
    // Invalidate all event queries for this project (includes calendar views with date params)
    queryClient.invalidateQueries({ queryKey: ['events', projectId] });
    // Also invalidate calendar-tasks since calendar shows both
    queryClient.invalidateQueries({ queryKey: ['calendar-tasks', projectId] });
  };

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof eventsApi.create>[1]) =>
      eventsApi.create(projectId, data),
    onSuccess: () => {
      invalidateEventQueries();
      handleClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof eventsApi.update>[1]) =>
      eventsApi.update(event!.id, data),
    onSuccess: () => {
      invalidateEventQueries();
      handleClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => eventsApi.delete(event!.id),
    onSuccess: () => {
      invalidateEventQueries();
      handleClose();
    },
  });

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setStartDate('');
    setStartTime('09:00');
    setEndDate('');
    setEndTime('10:00');
    setIsAllDay(false);
    setColor('');
    setHasEndDate(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !startDate) return;

    // Build start datetime
    const startDateTime = isAllDay
      ? `${startDate}T00:00:00`
      : `${startDate}T${startTime}:00`;

    // Build end datetime if provided
    let endDateTime: string | undefined;
    if (hasEndDate && endDate) {
      endDateTime = isAllDay
        ? `${endDate}T23:59:59`
        : `${endDate}T${endTime}:00`;
    }

    const data = {
      title: title.trim(),
      description: description.trim() || undefined,
      startDate: new Date(startDateTime).toISOString(),
      endDate: endDateTime ? new Date(endDateTime).toISOString() : undefined,
      isAllDay,
      color: color || undefined,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-elevated w-full max-w-md animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-grey-100">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-accent" />
            <h2 className="text-lg font-semibold text-secondary">
              {isEditing ? 'Edit Event' : 'New Event'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 text-grey-400 hover:text-secondary hover:bg-grey-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-grey-500 mb-1.5">
              Event Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's happening?"
              className="input text-sm"
              autoFocus
              required
            />
          </div>

          {/* All Day Toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsAllDay(!isAllDay)}
              className={cn(
                'relative w-10 h-5 rounded-full transition-colors',
                isAllDay ? 'bg-accent' : 'bg-grey-200'
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform',
                  isAllDay ? 'left-5' : 'left-0.5'
                )}
              />
            </button>
            <span className="text-sm text-grey-600">All day</span>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-grey-500 mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input text-sm"
                required
              />
            </div>
            {!isAllDay && (
              <div>
                <label className="block text-xs font-medium text-grey-500 mb-1.5">
                  Start Time
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="input text-sm"
                />
              </div>
            )}
          </div>

          {/* End Date Toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setHasEndDate(!hasEndDate);
                if (!hasEndDate) {
                  setEndDate(startDate);
                }
              }}
              className={cn(
                'relative w-10 h-5 rounded-full transition-colors',
                hasEndDate ? 'bg-accent' : 'bg-grey-200'
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform',
                  hasEndDate ? 'left-5' : 'left-0.5'
                )}
              />
            </button>
            <span className="text-sm text-grey-600">Multi-day event</span>
          </div>

          {/* End Date & Time */}
          {hasEndDate && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-grey-500 mb-1.5">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  className="input text-sm"
                />
              </div>
              {!isAllDay && (
                <div>
                  <label className="block text-xs font-medium text-grey-500 mb-1.5">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="input text-sm"
                  />
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-grey-500 mb-1.5">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about this event..."
              rows={2}
              className="input text-sm resize-none"
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-xs font-medium text-grey-500 mb-2">
              Color
            </label>
            <div className="flex gap-2">
              {colorOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setColor(option.value)}
                  className={cn(
                    'w-6 h-6 rounded-full transition-all',
                    option.class,
                    color === option.value
                      ? 'ring-2 ring-offset-2 ring-grey-400 scale-110'
                      : 'opacity-60 hover:opacity-100'
                  )}
                  title={option.label}
                />
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            {isEditing ? (
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="text-sm text-primary hover:text-primary-600 flex items-center gap-1"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
                Delete
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !title.trim()}>
                {isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isEditing ? (
                  'Save'
                ) : (
                  'Create Event'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
